import { useEffect, useState, useMemo, useCallback } from 'react';
import { useStudent } from '../context/StudentContext';
import { scoresApi, type Score } from '../api';
import { Plus, Search, ArrowUpDown, Trash2, Edit, X, Upload, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import PullToRefresh from '../components/PullToRefresh';

interface ScoreFormData {
  exam_name: string;
  exam_date: string;
  exam_type: string;
  total_score: string;
  chinese_score: string;
  math_score: string;
  english_score: string;
  physics_score: string;
  chemistry_score: string;
  geography_score: string;
  history_score: string;
  politics_score: string;
  biology_score: string;
  sports_score: string;
  class_rank: string;
  grade_rank: string;
  notes: string;
}

const examTypes = ['月考', '期中', '期末', '统考', '模拟', '一模', '二模', '三模'];

const initialFormData: ScoreFormData = {
  exam_name: '',
  exam_date: new Date().toISOString().split('T')[0],
  exam_type: '月考',
  total_score: '',
  chinese_score: '',
  math_score: '',
  english_score: '',
  physics_score: '',
  chemistry_score: '',
  geography_score: '',
  history_score: '',
  politics_score: '',
  biology_score: '',
  sports_score: '',
  class_rank: '',
  grade_rank: '',
  notes: '',
};

// 高中科目配置 (9科: 语数英+物化地/物化生)
const seniorSubjects = [
  { value: '总分', label: '总分', key: 'total_score', color: '#3B82F6', maxScore: 750 },
  { value: '语文', label: '语文', key: 'chinese_score', color: '#EF4444', maxScore: 150 },
  { value: '数学', label: '数学', key: 'math_score', color: '#F59E0B', maxScore: 150 },
  { value: '英语', label: '英语', key: 'english_score', color: '#10B981', maxScore: 150 },
  { value: '物理', label: '物理', key: 'physics_score', color: '#8B5CF6', maxScore: 100 },
  { value: '化学', label: '化学', key: 'chemistry_score', color: '#EC4899', maxScore: 100 },
  { value: '地理', label: '地理', key: 'geography_score', color: '#06B6D4', maxScore: 100 },
];

// 初中科目配置 (10科: 语数英+物化生+政史地+体育)
const juniorSubjects = [
  { value: '总分', label: '总分', key: 'total_score', color: '#3B82F6', maxScore: 800 },
  { value: '语文', label: '语文', key: 'chinese_score', color: '#EF4444', maxScore: 150 },
  { value: '数学', label: '数学', key: 'math_score', color: '#F59E0B', maxScore: 150 },
  { value: '英语', label: '英语', key: 'english_score', color: '#10B981', maxScore: 150 },
  { value: '物理', label: '物理', key: 'physics_score', color: '#8B5CF6', maxScore: 100 },
  { value: '化学', label: '化学', key: 'chemistry_score', color: '#EC4899', maxScore: 100 },
  { value: '生物', label: '生物', key: 'biology_score', color: '#84CC16', maxScore: 100 },
  { value: '道法', label: '道法', key: 'politics_score', color: '#F97316', maxScore: 100 },
  { value: '历史', label: '历史', key: 'history_score', color: '#A855F7', maxScore: 100 },
  { value: '地理', label: '地理', key: 'geography_score', color: '#06B6D4', maxScore: 100 },
];

// 需要卷面分/折算分切换的科目
const convertibleSubjects = ['physics_score', 'chemistry_score', 'geography_score', 'history_score', 'politics_score', 'biology_score'];

export default function ScoreTracker() {
  const { currentStudent } = useStudent();
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<ScoreFormData>(initialFormData);
  
  // 批量导入
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchMode, setBatchMode] = useState<'text' | 'table'>('text');
  const [batchText, setBatchText] = useState('');
  const [batchRecords, setBatchRecords] = useState<ScoreFormData[]>([]);
  const [batchErrors, setBatchErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  
  // 搜索和排序
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: keyof Score; direction: 'asc' | 'desc' } | null>(null);
  
  // Tab切换
  const [activeTab, setActiveTab] = useState<'list' | 'chart'>('list');
  
  // 趋势图科目选择
  const [chartSubject, setChartSubject] = useState<string>('总分');
  
  // 卷面分/折算分切换
  const [useRawScores, setUseRawScores] = useState(false);

  const isJunior = currentStudent ? ((currentStudent as any).education_level === 'junior') : false;
  const subjectOptions = isJunior ? juniorSubjects : seniorSubjects;

  useEffect(() => {
    if (!currentStudent) return;
    loadScores();
  }, [currentStudent?.id]);

  useEffect(() => {
    // 重置图表科目为总分
    setChartSubject('总分');
  }, [currentStudent?.id]);

  const loadScores = useCallback(async () => {
    if (!currentStudent) return;
    setLoading(true);
    try {
      const res = await scoresApi.list({ student_id: currentStudent.id, latest: 50 });
      setScores(res.data.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [currentStudent?.id]);

  // 搜索和排序过滤
  const filteredScores = useMemo(() => {
    let result = [...scores];
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s => 
        s.exam_name.toLowerCase().includes(term) ||
        (s.exam_type?.toLowerCase().includes(term) ?? false) ||
        (s.notes?.toLowerCase().includes(term) ?? false)
      );
    }
    
    if (sortConfig) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key as keyof Score];
        const bVal = b[sortConfig.key as keyof Score];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [scores, searchTerm, sortConfig]);

  const handleSort = (key: keyof Score) => {
    setSortConfig(current => {
      if (current?.key === key) {
        if (current.direction === 'asc') return { key, direction: 'desc' };
        return null;
      }
      return { key, direction: 'asc' };
    });
  };

  const handleSubmit = async () => {
    if (!currentStudent) return;
    
    const data: any = {
      student_id: currentStudent.id,
      exam_name: formData.exam_name,
      exam_date: formData.exam_date,
      exam_type: formData.exam_type,
      total_score: parseInt(formData.total_score) || 0,
      chinese_score: parseInt(formData.chinese_score) || undefined,
      math_score: parseInt(formData.math_score) || undefined,
      english_score: parseInt(formData.english_score) || undefined,
      physics_score: parseInt(formData.physics_score) || undefined,
      chemistry_score: parseInt(formData.chemistry_score) || undefined,
      geography_score: parseInt(formData.geography_score) || undefined,
      history_score: parseInt(formData.history_score) || undefined,
      politics_score: parseInt(formData.politics_score) || undefined,
      biology_score: parseInt(formData.biology_score) || undefined,
      sports_score: parseInt(formData.sports_score) || undefined,
      class_rank: parseInt(formData.class_rank) || undefined,
      grade_rank: parseInt(formData.grade_rank) || undefined,
      notes: formData.notes || undefined,
    };

    try {
      if (editingId) {
        await scoresApi.update(editingId, data);
      } else {
        await scoresApi.create(data);
      }
      setShowDialog(false);
      setEditingId(null);
      setFormData(initialFormData);
      loadScores();
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (score: Score) => {
    setFormData({
      exam_name: score.exam_name,
      exam_date: score.exam_date,
      exam_type: score.exam_type || '月考',
      total_score: score.total_score.toString(),
      chinese_score: score.chinese_score?.toString() || '',
      math_score: score.math_score?.toString() || '',
      english_score: score.english_score?.toString() || '',
      physics_score: score.physics_score?.toString() || '',
      chemistry_score: score.chemistry_score?.toString() || '',
      geography_score: score.geography_score?.toString() || '',
      history_score: score.history_score?.toString() || '',
      politics_score: score.politics_score?.toString() || '',
      biology_score: score.biology_score?.toString() || '',
      sports_score: score.sports_score?.toString() || '',
      class_rank: score.class_rank?.toString() || '',
      grade_rank: score.grade_rank?.toString() || '',
      notes: score.notes || '',
    });
    setEditingId(score.id);
    setShowDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这条成绩记录吗？')) return;
    try {
      await scoresApi.delete(id);
      loadScores();
    } catch (e) {
      console.error(e);
    }
  };

  // 批量导入解析
  const parseBatchText = () => {
    const lines = batchText.trim().split('\n').filter(l => l.trim());
    const records: ScoreFormData[] = [];
    const errors: string[] = [];
    
    lines.forEach((line, i) => {
      let parts = line.split(/[,，\t\s]+/).filter(p => p.trim());
      
      if (parts.length >= 3) {
        const examName = parts[0].trim();
        const examDate = parts[1].match(/\d{4}-\d{2}-\d{2}/) ? parts[1] : new Date().toISOString().split('T')[0];
        const totalScore = parseInt(parts[parts.length - 2]) || parseInt(parts[2]) || 0;
        const gradeRank = parseInt(parts[parts.length - 1]) || parseInt(parts[3]) || 0;
        
        records.push({
          exam_name: examName,
          exam_date: examDate,
          exam_type: '月考',
          total_score: totalScore.toString(),
          grade_rank: gradeRank.toString(),
          chinese_score: '',
          math_score: '',
          english_score: '',
          physics_score: '',
          chemistry_score: '',
          geography_score: '',
          history_score: '',
          politics_score: '',
          biology_score: '',
          sports_score: '',
          class_rank: '',
          notes: '',
        });
      } else {
        errors.push(`第${i + 1}行格式不正确: ${line}`);
      }
    });
    
    setBatchRecords(records);
    setBatchErrors(errors);
  };

  const handleBatchSubmit = async () => {
    if (!currentStudent || batchRecords.length === 0) return;
    
    setImporting(true);
    try {
      const records = batchRecords.map(r => ({
        student_id: currentStudent.id,
        exam_name: r.exam_name,
        exam_date: r.exam_date,
        exam_type: r.exam_type || '月考',
        total_score: parseInt(r.total_score) || 0,
        grade_rank: parseInt(r.grade_rank) || undefined,
      }));
      
      await scoresApi.createBatch(currentStudent.id, records);
      setShowBatchDialog(false);
      setBatchText('');
      setBatchRecords([]);
      setBatchErrors([]);
      loadScores();
    } catch (e) {
      console.error(e);
    } finally {
      setImporting(false);
    }
  };

  const addEmptyRow = () => {
    setBatchRecords([...batchRecords, { ...initialFormData, exam_date: new Date().toISOString().split('T')[0] }]);
  };

  const updateBatchRecord = (index: number, field: keyof ScoreFormData, value: string) => {
    const updated = [...batchRecords];
    updated[index] = { ...updated[index], [field]: value };
    setBatchRecords(updated);
  };

  const removeBatchRecord = (index: number) => {
    setBatchRecords(batchRecords.filter((_, i) => i !== index));
  };

  // 图表数据
  const selectedSubject = subjectOptions.find(s => s.value === chartSubject);
  const chartData = useMemo(() => {
    return scores.slice(-12).reverse().map(s => {
      const data: Record<string, any> = {
        name: s.exam_name.length > 6 ? s.exam_name.slice(0, 6) : s.exam_name,
        总分: s.total_score,
        年级排名: s.grade_rank,
        预测位次: s.province_rank_estimate ?? null,
      };
      // 添加单科数据
      subjectOptions.forEach(opt => {
        if (opt.key !== 'total_score') {
          data[opt.value] = s[opt.key as keyof Score] || null;
        }
      });
      return data;
    });
  }, [scores, subjectOptions]);

  // 自动收缩Y轴范围
  const scoreDomain = useMemo((): [number, number] => {
    const values = chartData.map(d => d[chartSubject]).filter((v): v is number => v != null && v > 0);
    if (values.length < 2) {
      return [0, selectedSubject?.maxScore || 750];
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const padding = Math.max(range * 0.3, selectedSubject?.maxScore ? selectedSubject.maxScore * 0.03 : 5);
    return [Math.max(0, Math.floor(min - padding)), Math.ceil(max + padding)];
  }, [chartData, chartSubject, selectedSubject]);

  const chartRankData = useMemo(() => {
    return scores.slice(-12).reverse().map(s => ({
      name: s.exam_name.length > 6 ? s.exam_name.slice(0, 6) : s.exam_name,
      排名: s.grade_rank,
    }));
  }, [scores]);

  const SortHeader = ({ label, sortKey }: { label: string; sortKey: keyof Score }) => (
    <th 
      className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => handleSort(sortKey)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sortConfig?.key === sortKey ? 'text-blue-500' : 'text-gray-300'} />
      </div>
    </th>
  );

  // 获取科目显示分数（支持卷面分/折算分切换）
  const getSubjectScore = (score: Score, key: string): number | undefined => {
    if (useRawScores && convertibleSubjects.includes(key)) {
      const rawKey = `raw_${key}` as keyof Score;
      const val = score[rawKey] ?? score[key as keyof Score];
      return typeof val === 'number' ? val : (typeof val === 'string' ? parseFloat(val) : undefined);
    }
    const val = score[key as keyof Score];
    return typeof val === 'number' ? val : (typeof val === 'string' ? parseFloat(val) : undefined);
  };

  return (
    <PullToRefresh onRefresh={loadScores}>
    <div className="p-3 md:p-6 max-w-7xl mx-auto">
      {/* 顶部标题 */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-800">成绩轨迹</h1>
        <p className="text-gray-500 text-sm mt-1">
          {currentStudent?.name} · {currentStudent?.school} · {isJunior ? '初中' : '高中'} · 满分 {isJunior ? 800 : 750} 分 · 记录每一次考试
        </p>
      </div>

      {/* Tab切换 */}
      <div className="flex gap-4 mb-4 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('list')}
          className={`pb-2.5 px-1 text-sm font-medium transition-colors relative ${
            activeTab === 'list' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          成绩列表
          {activeTab === 'list' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
        </button>
        <button
          onClick={() => setActiveTab('chart')}
          className={`pb-2.5 px-1 text-sm font-medium transition-colors relative ${
            activeTab === 'chart' ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          趋势图表
          {activeTab === 'chart' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-full" />}
        </button>
      </div>

      {/* 操作栏 */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {/* 搜索 */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="搜索考试名称..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 w-full md:w-56 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          
          {/* 统计 */}
          <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-xl">
            共 <span className="font-medium text-gray-700">{filteredScores.length}</span> 条记录
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowBatchDialog(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Upload size={16} />
            批量导入
          </button>
          <button
            onClick={() => { setEditingId(null); setFormData(initialFormData); setShowDialog(true); }}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors shadow-sm"
          >
            <Plus size={16} />
            录入成绩
          </button>
        </div>
      </div>

      {activeTab === 'list' ? (
        /* 成绩列表 */
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <SortHeader label="考试名称" sortKey="exam_name" />
                  <SortHeader label="日期" sortKey="exam_date" />
                  <SortHeader label="类型" sortKey="exam_type" />
                  <SortHeader label="总分" sortKey="total_score" />
                  <SortHeader label="年级排名" sortKey="grade_rank" />
                  <SortHeader label="预测省位次" sortKey="province_rank_estimate" />
                  <th className="px-3 py-2.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    各科分数
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredScores.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center text-gray-400">
                      <div className="flex flex-col items-center">
                        <Search size={40} className="opacity-30 mb-3" />
                        <p className="text-sm">{searchTerm ? '没有找到匹配的成绩记录' : '暂无成绩记录'}</p>
                        {!searchTerm && (
                          <button 
                            onClick={() => { setEditingId(null); setFormData(initialFormData); setShowDialog(true); }}
                            className="mt-3 text-blue-500 text-sm hover:underline"
                          >
                            录入第一个成绩 →
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredScores.map((score, idx) => (
                    <tr key={score.id} className="hover:bg-gray-50/80 transition-colors">
                      <td className="px-3 py-3">
                        <div className="font-medium text-gray-800">{score.exam_name}</div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {score.exam_date}
                      </td>
                      <td className="px-3 py-3">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                          {score.exam_type}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-lg font-semibold text-gray-800">{score.total_score}</span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {score.grade_rank ? `第${score.grade_rank}名` : '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {score.province_rank_estimate ? `约第${score.province_rank_estimate.toLocaleString()}名` : '-'}
                      </td>
                      <td className="px-3 py-3 text-xs text-gray-500">
                        <div className="flex flex-wrap gap-1">
                          {score.chinese_score && <span className="bg-gray-100 px-1.5 py-0.5 rounded">语{score.chinese_score}</span>}
                          {score.math_score && <span className="bg-gray-100 px-1.5 py-0.5 rounded">数{score.math_score}</span>}
                          {score.english_score && <span className="bg-gray-100 px-1.5 py-0.5 rounded">英{score.english_score}</span>}
                          {getSubjectScore(score, 'physics_score') && <span className="bg-gray-100 px-1.5 py-0.5 rounded">物{getSubjectScore(score, 'physics_score')}</span>}
                          {getSubjectScore(score, 'chemistry_score') && <span className="bg-gray-100 px-1.5 py-0.5 rounded">化{getSubjectScore(score, 'chemistry_score')}</span>}
                          {getSubjectScore(score, 'geography_score') && <span className="bg-gray-100 px-1.5 py-0.5 rounded">地{getSubjectScore(score, 'geography_score')}</span>}
                          {isJunior && getSubjectScore(score, 'history_score') && <span className="bg-gray-100 px-1.5 py-0.5 rounded">史{getSubjectScore(score, 'history_score')}</span>}
                          {isJunior && getSubjectScore(score, 'politics_score') && <span className="bg-gray-100 px-1.5 py-0.5 rounded">政{getSubjectScore(score, 'politics_score')}</span>}
                          {isJunior && getSubjectScore(score, 'biology_score') && <span className="bg-gray-100 px-1.5 py-0.5 rounded">生{getSubjectScore(score, 'biology_score')}</span>}
                          {isJunior && score.sports_score && <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded">体{score.sports_score}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEdit(score)}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => handleDelete(score.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* 趋势图表 */
        <div className="space-y-4">
          {/* 科目选择器 */}
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700 mr-2">选择科目：</span>
              {subjectOptions.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setChartSubject(opt.value)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    chartSubject === opt.value
                      ? 'text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={chartSubject === opt.value ? { backgroundColor: opt.color } : {}}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {chartData.length > 0 ? (
            <>
              {/* 分数趋势 */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-800 mb-4">{chartSubject}分数趋势</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis domain={scoreDomain} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => v ? String(v) : '-'} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey={chartSubject} 
                      stroke={selectedSubject?.color || '#3B82F6'} 
                      strokeWidth={2.5} 
                      dot={{ r: 4 }} 
                      name={chartSubject}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* 排名趋势 */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-800 mb-4">{chartSubject}排名趋势</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartRankData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis domain={['auto', 'auto']} reversed tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => v ? `第${v}名` : '-'} />
                    <Bar dataKey="排名" fill={selectedSubject?.color || '#8B5CF6'} radius={[4, 4, 0, 0]} name="年级排名" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* 预测省位次趋势 */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-800 mb-4">预测省位次趋势</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis domain={['auto', 'auto']} reversed tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => v ? `第${v.toLocaleString()}名` : '-'} />
                    <Legend />
                    <Line type="monotone" dataKey="预测位次" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} connectNulls={false} />
                  </LineChart>
                </ResponsiveContainer>
                {scores.filter(s => s.province_rank_estimate).length === 0 && (
                  <p className="text-center text-sm text-gray-500 mt-2">暂无预测省位次数据，可在录入成绩时填写"预测省位次"字段</p>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl p-16 text-center text-gray-400">
              <TrendingUp size={48} className="opacity-30 mx-auto mb-4" />
              <p>暂无成绩数据，无法生成趋势图</p>
            </div>
          )}
        </div>
      )}

      {/* 单次录入对话框 */}
      {showDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowDialog(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl mx-3 md:mx-0" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-semibold text-lg text-gray-800">{editingId ? '编辑成绩' : '录入成绩'}</h3>
              <button onClick={() => setShowDialog(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 md:p-6 space-y-4">
              {/* 考试名称和日期 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">考试名称 *</label>
                  <input
                    type="text"
                    value={formData.exam_name}
                    onChange={e => setFormData({ ...formData, exam_name: e.target.value })}
                    placeholder="如：高二上月考一"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">考试日期 *</label>
                  <input
                    type="date"
                    value={formData.exam_date}
                    onChange={e => setFormData({ ...formData, exam_date: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* 考试类型 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">考试类型</label>
                <div className="flex flex-wrap gap-2">
                  {examTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => setFormData({ ...formData, exam_type: type })}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        formData.exam_type === type
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* 总分和排名 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">总分 *</label>
                  <input
                    type="number"
                    value={formData.total_score}
                    onChange={e => setFormData({ ...formData, total_score: e.target.value })}
                    placeholder={`满分 ${isJunior ? 800 : 750}`}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">年级排名</label>
                  <input
                    type="number"
                    value={formData.grade_rank}
                    onChange={e => setFormData({ ...formData, grade_rank: e.target.value })}
                    placeholder="如：280"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* 各科分数 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">各科分数（选填）</label>
                <div className={`grid gap-3 ${isJunior ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-2 md:grid-cols-3'}`}>
                  <input
                    type="number"
                    value={formData.chinese_score}
                    onChange={e => setFormData({ ...formData, chinese_score: e.target.value })}
                    placeholder="语文"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    value={formData.math_score}
                    onChange={e => setFormData({ ...formData, math_score: e.target.value })}
                    placeholder="数学"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    value={formData.english_score}
                    onChange={e => setFormData({ ...formData, english_score: e.target.value })}
                    placeholder="英语"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    value={formData.physics_score}
                    onChange={e => setFormData({ ...formData, physics_score: e.target.value })}
                    placeholder="物理"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  <input
                    type="number"
                    value={formData.chemistry_score}
                    onChange={e => setFormData({ ...formData, chemistry_score: e.target.value })}
                    placeholder="化学"
                    className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                  {isJunior ? (
                    <>
                      <input
                        type="number"
                        value={formData.biology_score}
                        onChange={e => setFormData({ ...formData, biology_score: e.target.value })}
                        placeholder="生物"
                        className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                      <input
                        type="number"
                        value={formData.politics_score}
                        onChange={e => setFormData({ ...formData, politics_score: e.target.value })}
                        placeholder="道法"
                        className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                      <input
                        type="number"
                        value={formData.history_score}
                        onChange={e => setFormData({ ...formData, history_score: e.target.value })}
                        placeholder="历史"
                        className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                      <input
                        type="number"
                        value={formData.geography_score}
                        onChange={e => setFormData({ ...formData, geography_score: e.target.value })}
                        placeholder="地理"
                        className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </>
                  ) : (
                    <input
                      type="number"
                      value={formData.geography_score}
                      onChange={e => setFormData({ ...formData, geography_score: e.target.value })}
                      placeholder="地理"
                      className="px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                  )}
                  {/* 体育：仅初中显示 */}
                  {isJunior && (
                    <input
                      type="number"
                      value={formData.sports_score}
                      onChange={e => setFormData({ ...formData, sports_score: e.target.value })}
                      placeholder="体育"
                      className="px-3 py-2 text-sm border border-green-200 bg-green-50/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                    />
                  )}
                </div>
              </div>

              {/* 卷面分/折算分切换 */}
              {isJunior && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                  <span className="text-sm font-medium text-gray-700">卷面分/折算分：</span>
                  <button
                    type="button"
                    onClick={() => setUseRawScores(!useRawScores)}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                      useRawScores
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {useRawScores ? '卷面分' : '折算分'}
                  </button>
                  <span className="text-xs text-gray-500">
                    {useRawScores ? '显示原始卷面分数' : '显示折算后分数（用于总分计算）'}
                  </span>
                </div>
              )}

              {/* 班级排名和备注 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">班级排名</label>
                  <input
                    type="number"
                    value={formData.class_rank}
                    onChange={e => setFormData({ ...formData, class_rank: e.target.value })}
                    placeholder="选填"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">备注</label>
                  <input
                    type="text"
                    value={formData.notes}
                    onChange={e => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="选填"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSubmit}
                disabled={!formData.exam_name || !formData.total_score}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingId ? '保存修改' : '确认录入'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 批量导入对话框 */}
      {showBatchDialog && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowBatchDialog(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
              <h3 className="font-semibold text-lg text-gray-800">批量导入成绩</h3>
              <button onClick={() => setShowBatchDialog(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            {/* 模式切换 */}
            <div className="px-6 py-4 border-b border-gray-100">
              <div className="flex gap-2">
                <button
                  onClick={() => setBatchMode('text')}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    batchMode === 'text' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  文本粘贴
                </button>
                <button
                  onClick={() => setBatchMode('table')}
                  className={`px-4 py-2 text-sm font-medium rounded-xl transition-colors ${
                    batchMode === 'table' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  表格编辑
                </button>
              </div>
            </div>

            <div className="p-6">
              {batchMode === 'text' ? (
                /* 文本粘贴模式 */
                <div>
                  <div className="mb-4 p-3 bg-blue-50 rounded-xl text-sm text-blue-700">
                    <p className="font-medium mb-1">支持格式：</p>
                    <p className="text-blue-600">考试名称,日期,总分,年级排名</p>
                    <p className="text-blue-600 mt-1">例如：初二上月考一,2024-11-15,680,120</p>
                  </div>
                  <textarea
                    value={batchText}
                    onChange={e => setBatchText(e.target.value)}
                    placeholder="粘贴多行成绩数据，每行一条记录..."
                    className="w-full h-48 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-mono"
                  />
                  <button
                    onClick={parseBatchText}
                    disabled={!batchText.trim()}
                    className="mt-3 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-50"
                  >
                    解析数据
                  </button>
                </div>
              ) : (
                /* 表格编辑模式 */
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm text-gray-600">点击下方按钮添加行，或直接编辑</p>
                    <button
                      onClick={addEmptyRow}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                    >
                      <Plus size={14} /> 添加行
                    </button>
                  </div>
                  
                  {batchRecords.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      <p>暂无数据，请添加行</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-2 py-2 text-left font-medium text-gray-600">考试名称</th>
                            <th className="px-2 py-2 text-left font-medium text-gray-600">日期</th>
                            <th className="px-2 py-2 text-left font-medium text-gray-600">类型</th>
                            <th className="px-2 py-2 text-left font-medium text-gray-600">总分</th>
                            <th className="px-2 py-2 text-left font-medium text-gray-600">年级排名</th>
                            <th className="px-2 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {batchRecords.map((record, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="px-2 py-2">
                                <input
                                  type="text"
                                  value={record.exam_name}
                                  onChange={e => updateBatchRecord(idx, 'exam_name', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="date"
                                  value={record.exam_date}
                                  onChange={e => updateBatchRecord(idx, 'exam_date', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <select
                                  value={record.exam_type}
                                  onChange={e => updateBatchRecord(idx, 'exam_type', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                                >
                                  {examTypes.map(t => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  value={record.total_score}
                                  onChange={e => updateBatchRecord(idx, 'total_score', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  type="number"
                                  value={record.grade_rank}
                                  onChange={e => updateBatchRecord(idx, 'grade_rank', e.target.value)}
                                  className="w-full px-2 py-1 text-sm border border-gray-200 rounded"
                                />
                              </td>
                              <td className="px-2 py-2">
                                <button
                                  onClick={() => removeBatchRecord(idx)}
                                  className="p-1 text-gray-400 hover:text-red-500"
                                >
                                  <X size={14} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* 解析结果预览 */}
              {batchRecords.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      解析结果：{batchRecords.length} 条记录
                    </p>
                    <button
                      onClick={() => { setBatchRecords([]); setBatchErrors([]); }}
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      清空
                    </button>
                  </div>
                  
                  {batchErrors.length > 0 && (
                    <div className="mb-3 p-3 bg-red-50 rounded-xl text-sm text-red-700">
                      {batchErrors.map((err, i) => <p key={i}>{err}</p>)}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setShowBatchDialog(false)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleBatchSubmit}
                disabled={batchRecords.length === 0 || importing}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save size={16} />
                {importing ? '导入中...' : `导入 ${batchRecords.length} 条记录`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}

function TrendingUp({ size, className }: { size?: number; className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
      className={className}
    >
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  );
}
